/**
 * iter-381 — Reusable "Quick Create Project" dialog.
 *
 * Used inside the Create-BOQ flow (BOQWorksheets.js) so admins can spin
 * up a new project without navigating away from the BOQ dialog. The
 * dialog captures the essential fields (name, client, budget, start/end
 * dates, project number) and posts to `POST /api/projects` with sensible
 * defaults for the rest (pricing model = vendor_to_client, markup = 20%,
 * status = pending).
 *
 * Embedded inside is the "Add New Client" sub-dialog. Email is OPTIONAL
 * (a placeholder is auto-generated if left blank) — iter-381 removed the
 * asterisk and `required` attribute per user request.
 *
 * Props:
 *   open              — bool, dialog visibility
 *   onOpenChange(v)   — parent state setter
 *   onProjectCreated(project) — callback fired with the created project
 *                       (fields: id, name, …). Parent uses it to
 *                       auto-select the fresh project.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Building, Home, Plus, User } from 'lucide-react';
import { API } from '@/App';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import SearchableCombobox from '@/components/SearchableCombobox';

const EMPTY_PROJECT = {
  name: '',
  client_id: '',
  budget: '',
  start_date: '',
  end_date: '',
  project_number: '',
  location: '',
};
const EMPTY_CLIENT = {
  name: '',
  email: '',
  phone: '',
  client_type: 'residential',
};

export default function CreateProjectDialog({ open, onOpenChange, onProjectCreated }) {
  const [clients, setClients] = useState([]);
  const [formData, setFormData] = useState(EMPTY_PROJECT);
  const [creating, setCreating] = useState(false);
  const [clientDialogOpen, setClientDialogOpen] = useState(false);
  const [newClient, setNewClient] = useState(EMPTY_CLIENT);
  const [creatingClient, setCreatingClient] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const r = await axios.get(`${API}/users`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        setClients((r.data || []).filter((u) => u.role === 'client'));
      } catch {
        // silent — user can still create a new client from here
      }
    })();
  }, [open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || formData.name.trim().length < 2) {
      toast.error('Project name is required (min 2 chars)');
      return;
    }
    if (!formData.client_id) { toast.error('Please select or create a client'); return; }
    if (!formData.budget || parseFloat(formData.budget) <= 0) { toast.error('Please enter a valid budget'); return; }
    if (formData.start_date && formData.end_date && new Date(formData.end_date) <= new Date(formData.start_date)) { toast.error('End date must be after start date'); return; }

    setCreating(true);
    try {
      const resp = await axios.post(`${API}/projects`, {
        ...formData,
        name: formData.name.trim(),
        budget: parseFloat(formData.budget),
        location: (formData.location || '').trim() || null,
        project_number: (formData.project_number || '').trim() || null,
        pricing_model: 'client_to_vendor',
        markup_percentage: 20,
        vendor_markdown_percentage: 20,
        admin_markup_percentage: 20,
        client_markup_percentage: 20,
        status: 'pending',
        start_date: formData.start_date ? new Date(formData.start_date).toISOString() : null,
        end_date: formData.end_date ? new Date(formData.end_date).toISOString() : null,
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      toast.success('Project created');
      onOpenChange?.(false);
      setFormData(EMPTY_PROJECT);
      onProjectCreated?.(resp.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create project');
    } finally {
      setCreating(false);
    }
  };

  const handleCreateClient = async (e) => {
    e.preventDefault();
    if (!newClient.name || newClient.name.trim().length < 2) {
      toast.error('Client name is required'); return;
    }
    setCreatingClient(true);
    try {
      // iter-381 fix: use the admin create-client endpoint (bypasses email
      // verification and is the same path used by the Projects page).
      // Backend returns the user record directly (no `user` wrapper).
      const resp = await axios.post(`${API}/admin/create-client`, {
        name: newClient.name.trim(),
        // iter-381 fix: `.local` TLDs fail EmailStr validation on the
        // backend (`admin/create-client` uses strict email parsing).
        // Fall back to `noreply+<ts>@bellacasa.example` which passes
        // validation while remaining obviously synthetic.
        email: newClient.email?.trim() || `noreply+${Date.now()}@bellacasa.example`,
        phone: newClient.phone?.trim() || null,
        password: 'Client123!',
        role: 'client',
        client_type: newClient.client_type,
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      if (!resp.data || !resp.data.id) throw new Error('Client creation returned no id');
      toast.success('Client added');
      setClients((prev) => [...prev, resp.data]);
      setFormData((prev) => ({ ...prev, client_id: resp.data.id }));
      setClientDialogOpen(false);
      setNewClient(EMPTY_CLIENT);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add client');
    } finally {
      setCreatingClient(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-lg" data-testid="quick-create-project-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg flex items-center gap-2">
              <Plus size={18} /> Create New Project
            </DialogTitle>
            <p className="text-xs text-stone-500">
              Quick create — capture the essentials now and edit markup / description later from the Projects page.
            </p>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-3">
            <div>
              <Label>Project Name *</Label>
              <Input
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g. Whitefield Villa Renovation"
                data-testid="qcp-name-input"
              />
            </div>
            <div>
              <Label>Client *</Label>
              <SearchableCombobox
                options={clients.map((c) => ({ value: c.id, label: `${c.name}${c.phone ? ` · ${c.phone}` : ''}` }))}
                value={formData.client_id}
                onValueChange={(val) => setFormData({ ...formData, client_id: val })}
                placeholder="Search or create client…"
                createNewText="+ Add New Client"
                onCreateNew={() => setClientDialogOpen(true)}
                data-testid="qcp-client-picker"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Budget (₹) *</Label>
                <Input
                  required
                  type="number"
                  min="0"
                  step="1"
                  value={formData.budget}
                  onChange={(e) => setFormData({ ...formData, budget: e.target.value })}
                  placeholder="1000000"
                  data-testid="qcp-budget-input"
                />
              </div>
              <div>
                <Label>Project Number</Label>
                <Input
                  value={formData.project_number}
                  onChange={(e) => setFormData({ ...formData, project_number: e.target.value })}
                  placeholder="Auto if blank"
                  data-testid="qcp-projnum-input"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Start Date <span className="text-stone-400 font-normal">(optional)</span></Label>
                <Input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  data-testid="qcp-startdate-input"
                />
              </div>
              <div>
                <Label>End Date <span className="text-stone-400 font-normal">(optional)</span></Label>
                <Input
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                  data-testid="qcp-enddate-input"
                />
              </div>
            </div>
            <div>
              <Label>Location</Label>
              <Input
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                placeholder="e.g. Whitefield, Bengaluru (optional)"
                data-testid="qcp-location-input"
              />
            </div>
            <div className="flex justify-end gap-2 pt-2 border-t">
              <Button type="button" variant="outline" onClick={() => onOpenChange?.(false)} disabled={creating}>Cancel</Button>
              <Button
                type="submit"
                disabled={creating}
                className="bg-stone-900 hover:bg-stone-800 text-white"
                data-testid="qcp-create-btn"
              >
                <Plus size={14} className="mr-1" />
                {creating ? 'Creating…' : 'Create Project'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add New Client sub-dialog. Email is intentionally NOT marked
          required (iter-381) — the API auto-fills a placeholder. */}
      <Dialog open={clientDialogOpen} onOpenChange={setClientDialogOpen}>
        <DialogContent className="max-w-md" data-testid="qcp-add-client-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg flex items-center gap-2">
              <User size={18} /> Add New Client
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateClient} className="space-y-3">
            <div>
              <Label>Client Name *</Label>
              <Input
                required
                value={newClient.name}
                onChange={(e) => setNewClient({ ...newClient, name: e.target.value })}
                placeholder="e.g. John Smith"
                data-testid="qcp-client-name-input"
              />
            </div>
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={newClient.email}
                onChange={(e) => setNewClient({ ...newClient, email: e.target.value })}
                placeholder="client@example.com (optional)"
                data-testid="qcp-client-email-input"
              />
              <p className="text-[10px] text-stone-400 mt-0.5">Optional — a placeholder email is auto-generated if left blank.</p>
            </div>
            <div>
              <Label>Phone</Label>
              <Input
                value={newClient.phone}
                onChange={(e) => setNewClient({ ...newClient, phone: e.target.value })}
                placeholder="9876543210 (optional)"
                data-testid="qcp-client-phone-input"
              />
            </div>
            <div>
              <Label>Client Type *</Label>
              <div className="grid grid-cols-2 gap-2 mt-1">
                {[
                  { key: 'residential', icon: Home, label: 'Residential', sub: 'Homes & Apartments' },
                  { key: 'commercial', icon: Building, label: 'Commercial', sub: 'Offices & Retail' },
                ].map(({ key, icon: Icon, label, sub }) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setNewClient({ ...newClient, client_type: key })}
                    className={`p-3 rounded-lg border-2 flex flex-col items-center gap-1 transition-all ${
                      newClient.client_type === key
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-stone-200 hover:border-stone-300'
                    }`}
                  >
                    <Icon size={20} />
                    <span className="text-sm font-medium">{label}</span>
                    <span className="text-[10px] text-stone-500">{sub}</span>
                  </button>
                ))}
              </div>
            </div>
            <p className="text-[11px] text-stone-500 bg-stone-50 p-2 rounded">
              A default password will be set. The client can change it on first login.
            </p>
            <div className="flex justify-end gap-2 pt-2 border-t">
              <Button type="button" variant="outline" onClick={() => setClientDialogOpen(false)} disabled={creatingClient}>Cancel</Button>
              <Button
                type="submit"
                disabled={creatingClient || !newClient.name}
                className="bg-stone-900 hover:bg-stone-800 text-white"
                data-testid="qcp-add-client-submit"
              >
                <Plus size={14} className="mr-1" />
                {creatingClient ? 'Adding…' : 'Add Client'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
